// Static demo-mode fill-in data for Screenshots.component.jsx.
// Shapes must match exactly what the component's render path expects:
// - DEMO_MEMBERS_OPTIONS → ReportFiltersBar's member dropdown ({ value, label }).
// - DEMO_SESSIONS_BY_DAY → flattened into { dayLabel, dayKey, session } rows,
//   each `session` matching what SessionCard.component.jsx reads
//   (id, start, end, durationSec, idleSec, activityPct).
// - DEMO_SESSION_SCREENSHOTS → keyed by session id, each entry an array of
//   ScreenshotRow objects matching src/utils/screenshotPrivacy.js exactly:
//   { url, thumbUrl, timestamp, activityId, screenIndex }, consumed via
//   getScreenshotPreviewSrc/getScreenshotFullSrc/getScreenshotRevealKey.
import { createSequentialImagePicker } from '@/utils/demoScreenshotImages';

// Anchored to "today"/"yesterday" (UTC) so the demo never shows a stale
// fixed date — only the calendar dates shift with real time; the
// times-of-day stay fixed, matching the originally authored session shape.
const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n) => String(n).padStart(2, '0');
const isoDateFromDaysAgo = (daysAgo) => {
  const d = new Date(Date.now() - daysAgo * DAY_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};
const dayLabelFromDaysAgo = (daysAgo) =>
  new Date(Date.now() - daysAgo * DAY_MS).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
const TODAY_KEY = isoDateFromDaysAgo(0);
const TODAY_LABEL = dayLabelFromDaysAgo(0);
const YDAY_KEY = isoDateFromDaysAgo(1);
const YDAY_LABEL = dayLabelFromDaysAgo(1);

// Real demo screenshots, cycled in order (1st, 2nd, ... last, then back to
// the 1st) across entries below rather than picked randomly, so the
// sequence is stable and predictable across page loads. Image list and
// cycling logic live in the shared util so Timeline's demo data (which needs
// the same behavior) doesn't duplicate them.
const pickDemoScreenshotImage = createSequentialImagePicker();

// Builds one ScreenshotRow, reusing the same next-in-sequence image for both
// url and thumbUrl (as a real capture would have matching full/thumb assets).
function buildDemoScreenshotEntry(timestamp, activityId, screenIndex) {
  const image = pickDemoScreenshotImage();
  return { url: image, thumbUrl: image, timestamp, activityId, screenIndex };
}

// Consumed by ReportFiltersBar's member dropdown ({ value, label }).
export const DEMO_MEMBERS_OPTIONS = [
  {
    value: 'demo-user',
    label: 'Ryan Mitchell (EMP-1001)',
    employeeId: 'EMP-1001',
    rate: 0,
  },
  {
    value: 'demo-user-2',
    label: 'Rachel Turner (EMP-1002)',
    employeeId: 'EMP-1002',
    rate: 0,
  },
  {
    value: 'demo-user-3',
    label: 'Michael Chen (EMP-1003)',
    employeeId: 'EMP-1003',
    rate: 0,
  },
];

// Consumed by the flattenedSessions map → SessionCard: each day has
// dayKey/dayLabel and a `sessions` array of
// { id, start, end, durationSec, idleSec, activityPct }.
// Session ids are matched 1:1 against DEMO_SESSION_SCREENSHOTS keys below so
// screenshot lookups by session id actually resolve.
export const DEMO_SESSIONS_BY_DAY = [
  {
    dayKey: TODAY_KEY,
    dayLabel: TODAY_LABEL,
    sessions: [
      {
        id: 'demo-shot-session-2-1',
        start: `${TODAY_KEY}T09:02:00Z`,
        end: `${TODAY_KEY}T12:47:00Z`,
        durationSec: 13500,
        idleSec: 900,
        activityPct: 93,
      },
      {
        id: 'demo-shot-session-2-2',
        start: `${TODAY_KEY}T13:30:00Z`,
        end: `${TODAY_KEY}T17:15:00Z`,
        durationSec: 13500,
        idleSec: 1200,
        activityPct: 91,
      },
    ],
  },
  {
    dayKey: YDAY_KEY,
    dayLabel: YDAY_LABEL,
    sessions: [
      {
        id: 'demo-shot-session-1-1',
        start: `${YDAY_KEY}T09:15:00Z`,
        end: `${YDAY_KEY}T12:00:00Z`,
        durationSec: 9900,
        idleSec: 600,
        activityPct: 94,
      },
      {
        id: 'demo-shot-session-1-2',
        start: `${YDAY_KEY}T13:00:00Z`,
        end: `${YDAY_KEY}T16:30:00Z`,
        durationSec: 12600,
        idleSec: 1500,
        activityPct: 88,
      },
    ],
  },
];

// Keyed by session id (must match DEMO_SESSIONS_BY_DAY session ids above).
// Each row matches the ScreenshotRow shape from src/utils/screenshotPrivacy.js.
export const DEMO_SESSION_SCREENSHOTS = {
  'demo-shot-session-2-1': [
    buildDemoScreenshotEntry(
      `${TODAY_KEY}T09:30:00Z`,
      'demo-activity-2-1-a',
      0
    ),
    buildDemoScreenshotEntry(
      `${TODAY_KEY}T10:20:00Z`,
      'demo-activity-2-1-b',
      0
    ),
    buildDemoScreenshotEntry(
      `${TODAY_KEY}T11:45:00Z`,
      'demo-activity-2-1-c',
      0
    ),
  ],
  'demo-shot-session-2-2': [
    buildDemoScreenshotEntry(
      `${TODAY_KEY}T14:05:00Z`,
      'demo-activity-2-2-a',
      0
    ),
    buildDemoScreenshotEntry(
      `${TODAY_KEY}T15:40:00Z`,
      'demo-activity-2-2-b',
      0
    ),
    buildDemoScreenshotEntry(
      `${TODAY_KEY}T16:50:00Z`,
      'demo-activity-2-2-c',
      0
    ),
  ],
  'demo-shot-session-1-1': [
    buildDemoScreenshotEntry(`${YDAY_KEY}T09:45:00Z`, 'demo-activity-1-1-a', 0),
    buildDemoScreenshotEntry(`${YDAY_KEY}T11:10:00Z`, 'demo-activity-1-1-b', 0),
  ],
  'demo-shot-session-1-2': [
    buildDemoScreenshotEntry(`${YDAY_KEY}T13:25:00Z`, 'demo-activity-1-2-a', 0),
    buildDemoScreenshotEntry(`${YDAY_KEY}T15:00:00Z`, 'demo-activity-1-2-b', 0),
    buildDemoScreenshotEntry(`${YDAY_KEY}T16:10:00Z`, 'demo-activity-1-2-c', 0),
  ],
};
